import Wahanda from 'common/wahanda';
import { xhr } from 'common/xhr';
import {
  getCurrentVenueHashPrefix,
  navigateReportsHash,
  resolveReportsVenueId,
} from 'src/url-states/reports-hash';

export type SalesReportFilters = Record<string, string | number | Array<string | number> | null>;

type SalesReportData = Record<string, any>;

export type EnrichedLegacySalesReportRow = {
  label?: string;
  basic?: string | null;
  additional?: string | null;
  ttv?: string | null | { amount?: number };
  bookings?: number | string;
  showAdditionalServices?: boolean;
};

export type EnrichedLegacySalesReportView = {
  timeStamp: string;
  venueName: string;
  period: string;
  dates: string;
  total?: EnrichedLegacySalesReportRow | null;
  employees: EnrichedLegacySalesReportRow[];
  serviceGroups: EnrichedLegacySalesReportRow[];
  topServices: EnrichedLegacySalesReportRow[];
  channels: EnrichedLegacySalesReportRow[];
  showEmployees: boolean;
  showServiceGroups: boolean;
  showTopServices: boolean;
  showChannels: boolean;
  showAdditionalServices: boolean;
  fiveServices: boolean;
};

type WindowApp = {
  Api?: {
    wsUrl?: (path: string) => string;
  };
  config?: {
    get?: (key: string) => { name?: string } | undefined;
  };
};

interface SalesReportDateRange {
  from: Date;
  to: Date;
}

interface EnrichSalesReportTemplateDataParams {
  reportData: SalesReportData;
  dateRange: SalesReportDateRange;
  periodGranularity: string;
  venueName: string;
  currentTime: string;
}

const getWindowApp = (): WindowApp | undefined =>
  typeof window !== 'undefined' ? ((window as unknown) as { App?: WindowApp }).App : undefined;

const wsUrl = (path: string): string => {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const api = getWindowApp()?.Api;
  const resolve = api?.wsUrl;

  return typeof resolve === 'function' ? resolve.call(api, normalized) : `/api${normalized}`;
};

export const getSalesReportVenueName = (): string => {
  const venue = getWindowApp()?.config?.get?.('venue');
  return typeof venue?.name === 'string' ? venue.name : '';
};

const toQueryString = (filters: SalesReportFilters): string =>
  Object.entries(filters)
    .filter(([, rawValue]) => rawValue != null)
    .map(([name, rawValue]) => {
      const value = Array.isArray(rawValue) ? rawValue.join(',') : rawValue;
      return `${encodeURIComponent(name)}=${encodeURIComponent(String(value))}`;
    })
    .join('&');

const buildSalesReportUrl = (filters: SalesReportFilters): string => {
  const venueId = resolveReportsVenueId();
  const id = venueId != null && `${venueId}` !== '' ? venueId : '';
  const base = `/venue/${id}/reports/sales.json`;
  const queryString = toQueryString(filters);
  return wsUrl(queryString ? `${base}?${queryString}` : base);
};

export const fetchSalesReport = (filters: SalesReportFilters): JQueryXHR =>
  xhr.getWithPromise(buildSalesReportUrl(filters));

const SALES_REPORT_HASH_API_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export const parseSalesReportStartDateFromApiString = (apiDate: string): Date | null => {
  const m = SALES_REPORT_HASH_API_DATE.exec(apiDate);
  if (!m) {
    return null;
  }
  const parsed = Wahanda.Date.createVenueDate(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (!Wahanda.Date.isValidDate(parsed) || Wahanda.Date.toApiString(parsed) !== apiDate) {
    return null;
  }
  return parsed;
};

export const getSalesReportDateRange = (params: {
  startDate: Date;
  yesterday: Date;
  periodGranularity: string;
}): SalesReportDateRange => {
  const { startDate, yesterday, periodGranularity } = params;
  let endDate: Date;

  switch (periodGranularity) {
    case 'day':
      endDate = startDate;
      break;
    case 'week':
      endDate = Wahanda.Date.addDaysToDate(startDate, 6);
      break;
    case 'month': {
      endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);
      break;
    }
    default:
      endDate = yesterday;
  }

  endDate = Wahanda.Date.min(endDate, yesterday);

  return { from: startDate, to: endDate };
};

export const getSalesReportDateFilters = (dateRange: SalesReportDateRange): SalesReportFilters => ({
  'start-date': Wahanda.Date.toApiString(dateRange.from),
  'end-date': Wahanda.Date.toApiString(dateRange.to),
});

export const getSalesReportCurrentTime = (now: Date = new Date()): string => {
  const { date, time } = Wahanda.Date.formatToApiFullDate(now);
  return `${date} ${time}`;
};

export const buildSalesReportDateRangeTitle = (params: {
  periodGranularity: string;
  dateRange: SalesReportDateRange;
}): string => {
  const { periodGranularity, dateRange } = params;
  const template = Wahanda.lang.reports.salesReports.dateRange.current[periodGranularity];
  const from = Wahanda.Date.formatToDefaultDate(dateRange.from);
  const to = Wahanda.Date.formatToDefaultDate(dateRange.to);
  return template.replace('{{from}}', from).replace('{{to}}', to);
};

export const navigateSalesReportHash = (params: {
  periodGranularity: string;
  startDate: Date;
}): void => {
  const venueHash = getCurrentVenueHashPrefix();
  if (!venueHash) {
    return;
  }
  const path = `${venueHash}/sales/${params.periodGranularity}/${Wahanda.Date.toApiString(
    params.startDate,
  )}`;
  navigateReportsHash(path);
};

const formatOptionalPrice = (
  price: { amount: number } | null | undefined,
  additionalServiceCheck: boolean,
): { value: string | typeof price; showAdditionalServices: boolean } => {
  if (!price) {
    return { value: price, showAdditionalServices: false };
  }
  if (price.amount !== 0) {
    return {
      value: Wahanda.Currency.format(price.amount),
      showAdditionalServices: additionalServiceCheck,
    };
  }

  return { value: Wahanda.lang.reports.salesReports.zeroValue, showAdditionalServices: false };
};

const formatPricesCollection = (
  collection: SalesReportData[],
): { items: SalesReportData[]; showAdditionalServices: boolean } => {
  let showAdditionalServices = false;

  const items = collection.map((item) => {
    if (!item) {
      return item;
    }

    const basic = formatOptionalPrice(item.basic, false);
    const additional = formatOptionalPrice(item.additional, true);
    const ttv = formatOptionalPrice(item.ttv, false);

    showAdditionalServices = showAdditionalServices || additional.showAdditionalServices;

    return {
      ...item,
      basic: basic.value,
      additional: additional.value,
      ttv: ttv.value,
    };
  });

  return { items, showAdditionalServices };
};

const getPeriodAndDates = (
  periodGranularity: string,
  dateRange: SalesReportDateRange,
): Pick<SalesReportData, 'period' | 'dates'> => {
  switch (periodGranularity) {
    case 'day':
      return {
        period: Wahanda.lang.date.weekdays[dateRange.from.getDay()],
        dates: Wahanda.Date.formatToApiFullDate(dateRange.from).date,
      };
    case 'week': {
      const fromLabel = Wahanda.Date.formatToDefaultDate(dateRange.from);
      const toLabel = Wahanda.Date.formatToDefaultDate(dateRange.to);
      return {
        period: `${fromLabel} - ${toLabel}`,
        dates: Wahanda.Text.pluralizeNumber(
          Wahanda.Date.getDayDifference(dateRange.to, dateRange.from) + 1,
          [
            Wahanda.lang.reports.salesReports.day.toLowerCase(),
            Wahanda.lang.reports.salesReports.days.toLowerCase(),
          ],
        ),
      };
    }
    case 'month': {
      const fromLabel = Wahanda.Date.formatToDefaultDate(dateRange.from);
      const toLabel = Wahanda.Date.formatToDefaultDate(dateRange.to);
      return {
        period: Wahanda.lang.date.months[dateRange.from.getMonth()],
        dates: `${fromLabel} - ${toLabel}`,
      };
    }
    default:
      return { period: '', dates: '' };
  }
};

const enrichChannels = (channels: any[]): any[] =>
  channels.map((channel) => {
    const langChannel = Wahanda.lang.reports.salesReports.channels[channel.label];
    if (!langChannel) {
      return channel;
    }

    return {
      ...channel,
      label: langChannel.displayName,
    };
  });

const copyReportArray = (value: unknown): any[] => (Array.isArray(value) ? [...value] : []);

const attachGlobalShowAdditionalServices = (
  rows: SalesReportData[],
  showAdditionalServices: boolean,
): EnrichedLegacySalesReportRow[] =>
  rows.map((row) =>
    row && typeof row === 'object'
      ? ({ ...row, showAdditionalServices } as EnrichedLegacySalesReportRow)
      : (row as EnrichedLegacySalesReportRow),
  );

export const enrichSalesReportTemplateData = ({
  reportData,
  dateRange,
  periodGranularity,
  venueName,
  currentTime,
}: EnrichSalesReportTemplateDataParams): EnrichedLegacySalesReportView => {
  const employees = copyReportArray(reportData.employees);
  const serviceGroups = copyReportArray(reportData.serviceGroups);
  const topServices = copyReportArray(reportData.topServices);
  const channels = enrichChannels(copyReportArray(reportData.channels));
  const total = reportData.total ? [{ ...reportData.total }] : [];

  const formattedSections = [
    formatPricesCollection(total),
    formatPricesCollection(employees),
    formatPricesCollection(serviceGroups),
    formatPricesCollection(topServices),
    formatPricesCollection(channels),
  ];
  const [
    totalFormatted,
    employeesFormatted,
    serviceGroupsFormatted,
    topServicesFormatted,
    channelsFormatted,
  ] = formattedSections;
  const rangeData = getPeriodAndDates(periodGranularity, dateRange);
  const showAdditionalServices = formattedSections.some((s) => s.showAdditionalServices);
  const totalRowRaw = totalFormatted.items[0];
  const totalRow =
    totalRowRaw && typeof totalRowRaw === 'object'
      ? ({ ...totalRowRaw, showAdditionalServices } as EnrichedLegacySalesReportRow)
      : undefined;

  return {
    ...reportData,
    ...rangeData,
    venueName,
    timeStamp: currentTime,
    total: totalRow,
    employees: attachGlobalShowAdditionalServices(employeesFormatted.items, showAdditionalServices),
    serviceGroups: attachGlobalShowAdditionalServices(
      serviceGroupsFormatted.items,
      showAdditionalServices,
    ),
    topServices: attachGlobalShowAdditionalServices(
      topServicesFormatted.items,
      showAdditionalServices,
    ),
    channels: attachGlobalShowAdditionalServices(channelsFormatted.items, showAdditionalServices),
    showTopServices: topServices.length > 0,
    showServiceGroups: serviceGroups.length > 0,
    showEmployees: employees.length > 0,
    showChannels: channels.length > 0,
    fiveServices: topServices.length === 5,
    showAdditionalServices,
  } as EnrichedLegacySalesReportView;
};
